import { useMaxAvailableHeight } from "@redotech/react-util/available-height";
import { genericMemo } from "@redotech/react-util/component";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { useRef } from "react";
import {
  headerColorStyle,
  RedoTableProps,
  sizeToTemplateHeaderRowHeight,
  sizeToTemplateRowHeight,
  tableDimensionToGridTemplateStyles,
} from "./redo-table";
import * as redoTableCellsCss from "./redo-table-cells.module.css";
import { RedoTableExpandedRow, RedoTableRow } from "./redo-table-row";
import * as redoTableCss from "./redo-virtual-table.module.css";

// TODO: joe - make this the default table behavior
// A virtual table is just a table that only renders the rows in view + a few rows above and below
export const RedoVirtualTable = genericMemo(function RedoVirtualTable<DATA>({
  size = "sm",
  layout: { columns, headerColor = "grey" },
  rowsOfData,
  onRowClick,
  onContextMenu,
  onRowHovered,
  isRowClickable,
  rowsSelected,
  onVisibleHandlers,
  emptyStateContent = null,
  loadingStateContent = null,
  additionalClasses,
  footerRef,
  rowsExpansions,
  aggregationRow,
  maxAvailableHeight,
}: RedoTableProps<DATA> & { footerRef?: React.RefObject<HTMLDivElement> }) {
  const styleForGridColTemplate = columns
    .map((column) =>
      tableDimensionToGridTemplateStyles(column.widthMode || { type: "fill" }),
    )
    .join(" ");

  const styleForGridRow = sizeToTemplateRowHeight[size];
  const styleForHeaderRow = sizeToTemplateHeaderRowHeight[size];

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // TODO @joe - we need to get this working before we migrate other tables to RedoVirtualTable - but nobody is using this feature for now
  // const scrolled = useScrolled(scrollAreaRef.current);
  // useEffect(() => {
  //   if (!scrollAreaRef.current) {
  //     return;
  //   }
  //   if (scrolled.bottom) {
  //     onVisibleHandlers?.setBottomVisible(true);
  //   } else {
  //     onVisibleHandlers?.setBottomVisible(false);
  //   }
  //   if (scrolled.top) {
  //     onVisibleHandlers?.setTopVisible(true);
  //   } else {
  //     onVisibleHandlers?.setTopVisible(false);
  //   }
  // }, [scrolled, onVisibleHandlers]);

  const rowHeightPx = parseInt(styleForGridRow?.toString() || "48");
  const headerRowHeightPx = parseInt(styleForHeaderRow?.toString() || "36");

  const calculatedAvailableHeight = useMaxAvailableHeight({
    areaRef: scrollAreaRef,
    contentBelowRef: footerRef,
  });

  const availableHeight = maxAvailableHeight ?? calculatedAvailableHeight;

  const totalRowCount = rowsOfData.reduce((total, _, index) => {
    const numExpansions = rowsExpansions?.[index]?.expanded
      ? rowsExpansions[index].numExpansions || 0
      : 0;
    return total + 1 + numExpansions;
  }, 0);

  const rowMapping: {
    dataIndex: number;
    isExpanded: boolean;
    expansionIndex: number;
  }[] = [];

  for (const [dataIndex, _] of rowsOfData.entries()) {
    rowMapping.push({ dataIndex, isExpanded: false, expansionIndex: -1 });

    if (rowsExpansions?.[dataIndex]?.expanded) {
      const numExpansions = rowsExpansions[dataIndex].numExpansions || 0;
      for (let i = 0; i < numExpansions; i++) {
        rowMapping.push({ dataIndex, isExpanded: true, expansionIndex: i });
      }
    }
  }

  // https://tanstack.com/virtual/latest/docs/introduction
  const rowVirtualizer = useVirtualizer({
    count: totalRowCount,
    getScrollElement: () => scrollAreaRef.current,
    estimateSize: () => rowHeightPx,
    overscan: 10,
    paddingStart: headerRowHeightPx,
  });

  return (
    <div
      className={classNames(redoTableCss.virtualTableContainer)}
      ref={scrollAreaRef}
      style={{ height: `${availableHeight}px` }}
    >
      <div
        className={classNames(redoTableCss.table, additionalClasses)}
        role="table"
        style={{
          gridTemplateColumns: styleForGridColTemplate,
          gridTemplateRows: styleForHeaderRow,
          gridAutoRows: styleForGridRow,
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
      >
        <div
          className={classNames(redoTableCss.rowWrapper)}
          key="header"
          role="row"
        >
          {columns.map((column, columnIndex) => {
            const stickyLeftOffset = column.sticky
              ? columns
                  .slice(0, columnIndex)
                  .filter((c) => c.sticky)
                  .reduce((acc, c) => {
                    const width = c.widthMode;
                    if (width?.type === "fixed") {
                      return (
                        acc +
                        (typeof width.width === "number"
                          ? width.width
                          : parseInt(width.width as string) || 0)
                      );
                    }
                    if (
                      width?.type === "fill" &&
                      width.minLength !== undefined
                    ) {
                      return (
                        acc +
                        (typeof width.minLength === "number"
                          ? width.minLength
                          : parseInt(width.minLength as string) || 0)
                      );
                    }
                    return acc;
                  }, 0)
              : undefined;

            return (
              <div
                className={classNames(
                  redoTableCellsCss.cell,
                  redoTableCss.headerCell,
                  headerColorStyle[headerColor],
                  column.sticky && redoTableCss.stickyColumnHeaderCell,
                )}
                key={`header-${column.key ?? columnIndex}`}
                role="columnheader"
                style={{
                  height: styleForHeaderRow,
                  ...(stickyLeftOffset !== undefined && {
                    left: `${stickyLeftOffset}px`,
                  }),
                }}
              >
                <column.renderHeaderCell />
              </div>
            );
          })}
        </div>
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const mappingInfo = rowMapping[virtualRow.index];
          if (!mappingInfo) return null;

          const { dataIndex, isExpanded, expansionIndex } = mappingInfo;
          const row = rowsOfData[dataIndex];

          if (isExpanded) {
            const rowKey = `row-${dataIndex}-exp${expansionIndex}`;
            return (
              <div
                className={classNames(
                  redoTableCss.virtualRowWrapper,
                  redoTableCss.expandedRowWrapper,
                )}
                key={rowKey}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  gridTemplateColumns: styleForGridColTemplate,
                  height: `${rowHeightPx}px`,
                }}
              >
                <RedoTableExpandedRow<DATA>
                  columns={columns}
                  expansionIndex={expansionIndex}
                  key={rowKey}
                  onRowClick={onRowClick}
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  row={row}
                  rowIndex={dataIndex}
                />
              </div>
            );
          }

          return (
            <div
              className={classNames(redoTableCss.virtualRowWrapper)}
              key={`row-${dataIndex}`}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                gridTemplateColumns: styleForGridColTemplate,
                height: `${rowHeightPx}px`,
              }}
            >
              <RedoTableRow<DATA>
                columns={columns}
                isRowClickable={isRowClickable}
                key={`row-${dataIndex}`}
                onContextMenu={onContextMenu}
                onRowClick={onRowClick}
                onRowHovered={onRowHovered}
                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                row={row}
                rowIndex={dataIndex}
                rowSelected={rowsSelected?.[dataIndex]}
              />
            </div>
          );
        })}
      </div>
      {aggregationRow && (
        <div
          className={redoTableCss.stickyAggregationRow}
          style={{ gridTemplateColumns: styleForGridColTemplate }}
        >
          {aggregationRow}
        </div>
      )}
      {emptyStateContent}
      {loadingStateContent}
    </div>
  );
});
